import { useSession } from "@clerk/clerk-react";
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from "@heroui/react";
import { Check, Spinner } from "@phosphor-icons/react";
import { AlertCircle } from "assets/icons";
import ButtonNew from "components/buttonNew/buttonNew";
import { API_URL } from "constants/index";
import { useEffect, useState } from "react";
import ReactDatePicker from "react-datepicker";

// Type assertion for React 18 compatibility
const DatePicker = ReactDatePicker as any;

import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { RentalContract } from "shared/types/rental-contracts";

export interface TerminateContractModalProps {
    isOpen: boolean;
    onClose?: () => void;
    incomingContract: RentalContract | null;
    handleContractRefresh: () => void;
}

export const TerminateContractModal = ({ isOpen, onClose, incomingContract, handleContractRefresh }: TerminateContractModalProps) => {
    const [selectedContract, setSelectedContract] = useState<RentalContract | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [dateError, setDateError] = useState<string | null>(null);
    const { session } = useSession();

    useEffect(() => {
        setSelectedContract(incomingContract);
        if (incomingContract) {
            setEndDate(null);
            setDateError(null);
        }
    }, [incomingContract]);

    const handleClose = () => {
        if (isLoading) return;
        setSelectedContract(null);
        setEndDate(null);
        setDateError(null);
        onClose && onClose();
    };

    const handleDateChange = (date: Date | null) => {
        setEndDate(date);
        if (date) {
            setDateError(null);
        }
    };

    const terminateContract = async () => {
        if (!endDate) {
            setDateError('Selecteer een einddatum');
            return;
        }

        setIsLoading(true);

        try {
            await fetchWithAuth(`${API_URL}/rental/contract/${selectedContract?.id}/terminate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ endDate }),
            }, session);

            toast.success('Contract succesvol in opzeg gezet');
            handleContractRefresh();
            handleClose();
        } catch (error) {
            console.error('Error terminating contract', error);
            toast.error('Fout bij het opzeggen van het contract. Probeer opnieuw.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal
            onClose={handleClose}
            isOpen={isOpen}
            isDismissable={!isLoading}
            isKeyboardDismissDisabled={isLoading}
            className="font-normal text-neutral-primary"
            size="4xl"
            scrollBehavior="inside"
        >
            <ModalContent>
                {(onClose) => (
                    <>
                        <ModalHeader className="flex flex-col gap-1">
                            <p className="flex items-center gap-1 text-sm font-medium text-status-bad">
                                <AlertCircle className="h-[17px] w-[17px]" /> Beëindig contract / Laat opzeg ingaan
                            </p>
                        </ModalHeader>
                        <ModalBody>
                            <p className="text-neutral-primary">Ben je zeker dat je dit contract wil opzeggen?</p>
                            <p className="text-neutral-primary">Zo ja, wanneer zal het contract eindigen?</p>
                            <div className="space-y-1">
                                <DatePicker
                                    selected={endDate}
                                    onChange={handleDateChange}
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText="Selecteer einddatum"
                                    className="w-full rounded-sm bg-white p-2.5 text-neutral-primary shadow-form-element placeholder:text-neutral-secondary-mid focus:ring-3 focus:ring-opacity-50"
                                />
                                {dateError && (
                                    <p className="text-xs text-status-bad mt-1">{dateError}</p>
                                )}
                            </div>
                        </ModalBody>
                        <ModalFooter className="justify-start">
                            <ButtonNew
                                variant="primaryBrick"
                                onClick={terminateContract}
                                isDisabled={isLoading}
                                icon={isLoading ? <Spinner className="animate-spin" /> : <Check />}
                                label={isLoading ? 'Bezig...' : 'Laat opzeg ingaan'}
                            />
                            <ButtonNew
                                variant="secondaryNeutral"
                                onClick={onClose}
                                isDisabled={isLoading}
                                label="Annuleer"
                            />
                        </ModalFooter>
                    </>
                )}
            </ModalContent>
        </Modal>
    );
};
